import { html, LitElement, TemplateResult, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { iconUtils, SeverityLevel, SeverityLevelType, iconColorsMap } from "./iconUtils";

/**
 * @summary This component is used to display different levels of severity.
 * For accessibility purposes (color vision blindness) each severity level has a corresponding shape, maintaining the following colors:
 * - Critical (chevron shape): Red alert & chart 700 critical (--red-alert-chart-700-critical)
 * - High (triangle pointing up shape): Red alert 500 severe (--red-alert-500-severe)
 * - Medium (square shape): Orange alert & chart 100 medium (--orange-alert-chart-100)
 * - Low (triangle pointing down shape): Yellow alert 050 low warning (--yellow-alert-050)
 * - No threat (circle shape): Green pastel alert & chart 050 healthy (--green-pastel-alert-chart-050-healthy)
 * - Unknown (circle outline shape): Grey layout 200 disabled (--grey-layout-200-disabled)
 * @tag gz-severity-icon
 * @property level - The severity level. Can be one of the following: critical, high, medium, low, safe, unknown. The default value is unknown.
 * @property size - The size of the icon. Default is 10px.
 */
@customElement('gz-severity-icon')
export class GzSeverityIconWebComponent extends LitElement {
  static get styles() {
    return css`
      :host {
        display: inline;
        box-sizing: border-box;
      }
    `;
  }

  /**
   * This property sets the severity level of the icon. For accessibility purposes (color vision blindness) each severity level has a corresponding shape and color.
   */
  @property({ type: String, attribute: true, reflect: true })
  level: SeverityLevelType = SeverityLevel.CRITICAL;

  /**
   * This property sets the size of the icon. Default is 10px.
   */
  @property({ type: Number, attribute: true, reflect: true })
  size = 10;

  private getSeverityIcon(severityLevel: SeverityLevelType): TemplateResult {
    return html`${iconUtils.get(severityLevel)}`;
  }

  private getIconColor(severityLevel: SeverityLevelType): string {
    return iconColorsMap.get(severityLevel) || 'var(--grey-layout-200-disabled)';
  }


  render() {
    return html`
      <span>
        <gz-icon .size="${this.size}" .color="${this.getIconColor(this.level)}">${this.getSeverityIcon(this.level)}</gz-icon>
      </span>
    `;
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'gz-severity-icon': GzSeverityIconWebComponent;
  }
}
